import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  AssignmentTurnedIn as ResolvedIcon,
  HistoryToggleOff as NotResolvedIcon,
  NewReleases as NewIcon,
  Pending as InProgressIcon,
} from '@mui/icons-material';
import api from '../../config/api';
import { UnifiedFeedback } from '../../types/unifiedFeedback';

interface StatusOption {
  value: 'new' | 'in_progress' | 'resolved' | 'not_resolved';
  label: string;
  color: 'info' | 'warning' | 'success' | 'error';
  icon: React.ReactElement;
}

interface OutletPIC {
  user_id: string;
  manager_name: string;
  email: string;
  job_title: string;
}

export interface ComplaintStatusDialogProps {
  open: boolean;
  feedback: UnifiedFeedback | null;
  onClose: () => void;
  onSave: (id: string, updates: { status: string; note: string; outlet_pic_id?: string }) => void;
}

const statusOptions: StatusOption[] = [
  { value: 'new', label: 'New', color: 'info', icon: <NewIcon /> },
  { value: 'in_progress', label: 'In Progress', color: 'warning', icon: <InProgressIcon /> },
  { value: 'resolved', label: 'Resolved', color: 'success', icon: <ResolvedIcon /> },
  { value: 'not_resolved', label: 'Not Resolved', color: 'error', icon: <NotResolvedIcon /> },
];

export const ComplaintStatusDialog: React.FC<ComplaintStatusDialogProps> = ({
  open,
  feedback,
  onClose,
  onSave,
}) => {
  const [status, setStatus] = useState(feedback?.complaint_status || 'new');
  const [note, setNote] = useState(feedback?.complaint_note || '');
  const [outletPICs, setOutletPICs] = useState<OutletPIC[]>([]);
  const [selectedPIC, setSelectedPIC] = useState<string>('');
  const [loadingPICs, setLoadingPICs] = useState(false);
  const [userInfo, setUserInfo] = useState<{ user_id?: string } | null>(null);

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const response = await api.get('/auth/user-info');
        if (response.data.success) {
          setUserInfo(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching user info:', error);
      }
    };

    if (open) {
      void fetchUserInfo();
    }
  }, [open]);

  useEffect(() => {
    setStatus(feedback?.complaint_status || 'new');
    setNote(feedback?.complaint_note || '');
    setSelectedPIC('');

    if (open && feedback?.brand_id) {
      void fetchOutletPICs(feedback.brand_id);
    }
  }, [feedback, open]);

  useEffect(() => {
    if (status.toLocaleLowerCase() === 'new') {
      setSelectedPIC('');
    }
  }, [status]);

  const isAssignedOutletPIC = userInfo?.user_id === feedback?.outlet_pic_id;
  const availableStatusOptions = isAssignedOutletPIC
    ? statusOptions.filter((option) => ['resolved', 'not_resolved'].includes(option.value))
    : statusOptions;

  const fetchOutletPICs = async (brandId: string) => {
    setLoadingPICs(true);
    try {
      const response = await api.get('/brand-management/outlet-pics', {
        params: { brand_id: brandId, position_type: 'OTL' },
      });

      if (response.data.success) {
        const sortedPICs = [...response.data.data].sort((a, b) =>
          a.user_id.localeCompare(b.user_id)
        );
        setOutletPICs(sortedPICs);
      }
    } catch (error) {
      console.error('Error fetching outlet PICs:', error);
    } finally {
      setLoadingPICs(false);
    }
  };

  const handleSave = () => {
    if (!feedback?.id) {
      return;
    }

    const updates: { status: string; note: string; outlet_pic_id?: string } = {
      status,
      note,
    };

    if (status === 'in_progress' && selectedPIC) {
      updates.outlet_pic_id = selectedPIC;
    }

    if (status === 'new') {
      updates.outlet_pic_id = '';
    }

    onSave(feedback.id, updates);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Update Complaint Status</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 2 }}>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select value={status} onChange={(e) => setStatus(e.target.value)} label="Status">
              {availableStatusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {React.cloneElement(option.icon, { color: option.color, fontSize: 'small' })}
                    <span>{option.label}</span>
                  </Box>
                </MenuItem>
              ))}
            </Select>
            {isAssignedOutletPIC && (
              <FormHelperText>
                As the assigned outlet PIC, you can only change the status to Resolved or Not Resolved
              </FormHelperText>
            )}
          </FormControl>

          {status === 'in_progress' && !isAssignedOutletPIC && !feedback?.outlet_pic_name && (
            <FormControl fullWidth required>
              <InputLabel>Assign Outlet PIC</InputLabel>
              <Select
                value={selectedPIC}
                onChange={(e) => setSelectedPIC(e.target.value)}
                label="Assign Outlet PIC"
                disabled={loadingPICs}
              >
                {loadingPICs ? (
                  <MenuItem value="">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={20} />
                      <span>Loading...</span>
                    </Box>
                  </MenuItem>
                ) : outletPICs.length > 0 ? (
                  outletPICs.map((pic) => (
                    <MenuItem key={pic.user_id} value={pic.user_id}>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body2">{pic.user_id}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {pic.manager_name}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem value="">
                    <Typography color="error">No outlet PICs found</Typography>
                  </MenuItem>
                )}
              </Select>
              {status === 'in_progress' && !selectedPIC && (
                <FormHelperText error>Required when setting status to In Progress</FormHelperText>
              )}
            </FormControl>
          )}

          <TextField
            multiline
            rows={4}
            label="Notes"
            fullWidth
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Add notes about the resolution or next steps..."
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={status === 'in_progress' && !selectedPIC && !isAssignedOutletPIC && !feedback?.outlet_pic_name}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
